import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BackendResponseStatus, IBackendResponse } from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';
import { Observable, ReplaySubject, merge } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ZeroStorage } from '../amplify/zero-storage.service';
import { CrossTabsEventsService } from '../dom/cross-tabs-events.service';
import { HttpResponseHandleHelper } from '../http/http-response-handle-helper';
import { AuthService } from './auth.service';
import { PreAuthorizationSessionRequestData } from './pre-authorization-session-request-data';
import { PreAuthorizationSessionVerifyData } from './pre-authorization-session-verify-data';
import { PreAuthorizationSessionVerifyTanData } from './pre-authorization-session-verify-tan-data';
import { PreAuthorizedBaseData } from './pre-authorized-base-data';

@Injectable({
  providedIn: 'root',
})
export class SessionTanService {
  public sessionTanState = new ReplaySubject<IBackendResponse<boolean>>(1);
  public ttsEnabledState = new ReplaySubject<IBackendResponse<boolean>>(1);
  private lastServerResult: IBackendResponse<boolean>;
  private lastServerResultEventId: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    @Inject(LOCAL_STORAGE) private localStorage: StorageService,
    private crossTabsEventsService: CrossTabsEventsService,
  ) {
    this.authService.isLoggedIn$().subscribe((loggedIn) => {
      if (!loggedIn) {
        this.resetLastServerResult();
        if (this.sessionTanState.observers.length > 0) {
          this.sessionTanState.complete();
        }
        this.sessionTanState = new ReplaySubject<IBackendResponse<boolean>>(1);
      }
    });

    merge(
      this.crossTabsEventsService.getEventIdChange$(),
      this.crossTabsEventsService.getTradingSessionStateUpdatedInDifferentTab$(),
    ).subscribe(() => {
      this.isSessionAuthorized();
    });
  }

  public isSessionAuthorized(): Observable<IBackendResponse<boolean>> {
    const correspondingEventid = this.getCurrentEventId();
    if (!this.lastServerResult || this.lastServerResult.data === false || this.lastServerResultEventId !== correspondingEventid) {
      HttpResponseHandleHelper.handleResponse(this.http.get<boolean>(`${environment.apihost}/api/sessiontan/isauthorized`)).subscribe(
        (result) => {
          this.lastServerResult = result;
          this.lastServerResultEventId = correspondingEventid;
          this.sessionTanState.next(result);
        },
      );
    }

    return this.sessionTanState.asObservable();
  }

  public isTtsEnabled(): Observable<IBackendResponse<boolean>> {
    return HttpResponseHandleHelper.handleResponse(
      this.http.get<boolean>(`${environment.apihost}/api/sessiontan/isttsenabled`, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    );
  }

  public requestSessionTAN(tts?: boolean): Observable<IBackendResponse<boolean>> {
    if (tts === undefined || tts === null) {
      tts = false;
    }
    const httpParams = HttpResponseHandleHelper.skipGlobalHandler().append('useSpeech', `${tts}`);
    return HttpResponseHandleHelper.handleResponse(
      this.http.post<boolean>(`${environment.apihost}/api/sessiontan/requesttan`, null, { params: httpParams }),
    );
  }

  public verifySessionTAN(tan: string): Observable<IBackendResponse<boolean>> {
    const correspondingEventid = this.getCurrentEventId();
    return HttpResponseHandleHelper.handleResponse(
      this.http.post<boolean>(`${environment.apihost}/api/sessiontan/verifytan`, tan, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    ).pipe(
      tap((response) => {
        if (response.success && response.data) {
          if (!this.lastServerResult || this.lastServerResult.data !== response.data) {
            this.crossTabsEventsService.triggerTradingSessionStateUpdatedInDifferentTab();
          }
          this.lastServerResult = response;
          this.lastServerResultEventId = correspondingEventid;
          this.sessionTanState.next(response);
        }
      }),
    );
  }

  public requestAppAuth(serial: string): Observable<IBackendResponse<string>> {
    return HttpResponseHandleHelper.handleResponse(
      this.http.post(`${environment.apihost}/api/sessiontan/requestauth`, serial, {
        responseType: 'text',
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    );
  }

  public verifyAppAuth(authorizationId: string): Observable<IBackendResponse<boolean>> {
    const correspondingEventid = this.getCurrentEventId();
    return HttpResponseHandleHelper.handleResponse(
      this.http.post<boolean>(`${environment.apihost}/api/sessiontan/verifyauth`, authorizationId, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    ).pipe(
      tap((response) => {
        if (response.success && response.data) {
          if (!this.lastServerResult || this.lastServerResult.data !== response.data) {
            this.crossTabsEventsService.triggerTradingSessionStateUpdatedInDifferentTab();
          }
          this.lastServerResult = response;
          this.lastServerResultEventId = correspondingEventid;
          this.sessionTanState.next(response);
        }
      }),
    );
  }

  public revokeSession(): Observable<IBackendResponse<boolean>> {
    return HttpResponseHandleHelper.handleResponse(
      this.http.post<boolean>(`${environment.apihost}/api/sessiontan/revoke`, null, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    ).pipe(
      tap((response) => {
        this.resetLastServerResult();
        this.sessionTanState.next(response);
      }),
    );
  }

  public resetLastServerResult(): void {
    this.lastServerResult = undefined;
    this.lastServerResultEventId = undefined;
  }

  public getCurrentEventId(): string {
    return this.localStorage.get(ZeroStorage.KEY_EVENTID);
  }

  public requestPreAuthorizedAppAuth(preAuthData: PreAuthorizedBaseData, serial: string): Observable<IBackendResponse<string>> {
    const requestPayload = new PreAuthorizationSessionRequestData(
      preAuthData.type,
      preAuthData.hash,
      preAuthData.customerId,
      preAuthData.username,
      serial,
    );

    return HttpResponseHandleHelper.handleResponse(
      this.http.post(`${environment.apihost}${environment.oAuth2Endpoints.session}/requestauth`, requestPayload, { responseType: 'text' }),
    );
  }

  public verifyPreAuthorizedAppAuth(preAuthData: PreAuthorizedBaseData, authorizationId: string): Observable<IBackendResponse<boolean>> {
    const requestPayload = new PreAuthorizationSessionVerifyData(
      preAuthData.type,
      preAuthData.hash,
      preAuthData.customerId,
      preAuthData.username,
      authorizationId,
    );

    return HttpResponseHandleHelper.handleResponse(
      this.http.post<boolean>(`${environment.apihost}${environment.oAuth2Endpoints.session}/verifyauth`, requestPayload),
    ).pipe(
      tap((response) => {
        if (response.status === BackendResponseStatus.SUCCESS && response.data) {
          this.lastServerResult = response;
          this.sessionTanState.next(response);
        }
      }),
    );
  }

  public verifyPreAuthorizedSessionTAN(preAuthData: PreAuthorizedBaseData, tan: string): Observable<IBackendResponse<boolean>> {
    const requestPayload = new PreAuthorizationSessionVerifyTanData(
      preAuthData.type,
      preAuthData.hash,
      preAuthData.customerId,
      preAuthData.username,
      tan,
    );

    return HttpResponseHandleHelper.handleResponse(
      this.http.post<boolean>(`${environment.apihost}${environment.oAuth2Endpoints.session}/verifytan`, requestPayload),
    ).pipe(
      tap((response) => {
        if (response.status === BackendResponseStatus.SUCCESS && response.data) {
          this.lastServerResult = response;
          this.sessionTanState.next(response);
        }
      }),
    );
  }

  public isPreAuthorizedTtsEnabled(preAuthData: PreAuthorizedBaseData): Observable<IBackendResponse<boolean>> {
    return HttpResponseHandleHelper.handleResponse(
      this.http.get<boolean>(
        `${environment.apihost}${environment.oAuth2Endpoints.session}/isttsenabled?hash=${preAuthData.hash}&customerId=${preAuthData.customerId}&username=${preAuthData.username}`,
      ),
    );
  }

  public requestPreAuthorizedSessionTAN(preAuthData: PreAuthorizedBaseData, tts?: boolean): Observable<IBackendResponse<boolean>> {
    if (tts === undefined || tts === null) {
      tts = false;
    }

    const httpParams = new HttpParams().append('useSpeech', `${tts}`);
    const requestPayload = new PreAuthorizedBaseData(preAuthData.type, preAuthData.hash, preAuthData.customerId, preAuthData.username);

    return HttpResponseHandleHelper.handleResponse(
      this.http.post<boolean>(`${environment.apihost}${environment.oAuth2Endpoints.session}/requesttan`, requestPayload, {
        params: httpParams,
      }),
    );
  }
}
